import React, { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './Auth.css';
import { siteUrl } from '../native';
import InviteBanner from './InviteBanner';
import { arrival } from '../arrival';
import { isInviteCode } from '../invites';
import { saveMeta } from '../privateMeta';
import { Icon } from './ui';

const TERMS_VERSION = '2026-11-01';
const PRIVACY_VERSION = '2026-11-03.5';
const SAFETY_VERSION = '2026-08-27';

function Signup({ onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeSafety, setAgreeSafety] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Brief 020 (review F20): the name partners see, saved as `name` (no separate username).
  const validateName = (name) => {
    const n = name.trim();
    if (n.length < 2) return 'Please enter your name';
    if (n.length > 60) return 'Name must be 60 characters or fewer';
    return null;
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain a number';
    if (!/[!@#$%^&*]/.test(password)) {
      return 'Password must contain a special character (!@#$%^&*)';
    }
    return null;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    const nameError = validateName(formData.name);
    if (nameError) { setError(nameError); return; }

    const passwordError = validatePassword(formData.password);
    if (passwordError) { setError(passwordError); return; }

    if (!agreeTerms || !agreeSafety) {
      setError('Please check both boxes to continue');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const name = formData.name.trim();
      await updateProfile(userCredential.user, {
        displayName: name
      });

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        name,
        createdAt: new Date().toISOString(),
        profileComplete: false,
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
        safetyAcknowledgmentVersion: SAFETY_VERSION,
        age: null,
        gender: '',
        location: '',
        school: '',
        yearLevel: '',
        studyCategory: '',
        subjects: [],
        availabilityLocal: [],
        availabilityTz: '',
        strengths: [],
        wantsToLearn: [],
        languages: ['English'],
        profilePicture: '',
        reputation: {
          sessionsCompleted: 0,
          attendanceRate: 100,
          totalStudyHours: 0,
          currentStreak: 0
        }
      });

      // Came through an invite link (P-1): remember whose, privately, so setup can connect them.
      const { invite } = arrival();
      if (isInviteCode(invite)) {
        try { await saveMeta(userCredential.user.uid, { invitedBy: invite }); }
        catch (e) { console.warn('invite not recorded', e); }
      }

      try {
        await addDoc(collection(db, 'acceptances'), {
          userId: userCredential.user.uid,
          termsVersion: TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
          safetyAcknowledgmentVersion: SAFETY_VERSION,
          acceptedAt: serverTimestamp()
        });
      } catch (e) {
        console.warn('Acceptance record failed', e);
      }

    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError('Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const boxStyle = {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
    padding: '14px 16px',
    background: 'var(--surface)',
    border: '1px solid var(--line-strong)',
    borderRadius: '12px',
    marginBottom: '12px',
    color: 'var(--ink)',
    cursor: 'pointer',
    lineHeight: 1.45
  };

  const inputStyle = { marginTop: '0.2rem', width: '18px', height: '18px', flexShrink: 0, accentColor: 'var(--accent)', cursor: 'pointer' };

  return (
    <div className="auth-card">
      <div className="auth-header">
        <h1><img src="/logo.svg" alt="MedXMeet" className="auth-logo" /></h1>
        <p>Create your account to get started</p>
      </div>
      <InviteBanner />

      <form onSubmit={handleSignup} className="auth-form">
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="signup-name">Your name *</label>
          <input
            id="signup-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="First and last name"
            autoComplete="name"
            required
          />
          <small>Shown to your study partners.</small>
        </div>

        <div className="form-group">
          <label htmlFor="signup-email">Email *</label>
          <input
            id="signup-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            autoComplete="email"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="signup-password">Password *</label>
          <div className="password-field">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a strong password"
              autoComplete="new-password"
              required
            />
            <button type="button" className="password-eye" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword}>
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={20} />
            </button>
          </div>
          <small>Min 8 chars, uppercase, lowercase, number, special char</small>
        </div>

        <div style={{background:'var(--accent-tint)',color:'var(--ink)',borderRadius:'12px',padding:'14px 16px',marginBottom:'16px',fontSize:'14px',lineHeight:1.55}}>
          <strong>MedXMeet is for educational peer collaboration only.</strong> It is not designed to receive patient or confidential clinical information.
        </div>

        <label style={boxStyle}>
          <input type="checkbox" checked={agreeTerms} onChange={e=>{setAgreeTerms(e.target.checked);setError('');}} style={inputStyle} />
          <span style={{fontSize:'14px'}}>
            I agree to the <a href={siteUrl('/terms.html')} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600}}>Terms of Use</a> and acknowledge the <a href={siteUrl('/privacy.html')} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600}}>Privacy Policy</a>.
          </span>
        </label>

        <label style={boxStyle}>
          <input type="checkbox" checked={agreeSafety} onChange={e=>{setAgreeSafety(e.target.checked);setError('');}} style={inputStyle} />
          <span style={{fontSize:'14px'}}>
            I understand MedXMeet is a peer-study platform, not a medical or clinical service. I will not use it for medical advice, diagnosis, treatment, patient care, or emergencies, and I will not upload or share patient information, medical records, PHI, or confidential clinical data.
          </span>
        </label>

        <button type="submit" className="btn-primary" disabled={loading || !agreeTerms || !agreeSafety}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>

      <div className="auth-footer">
        <p>
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className="link-btn">
            Login
          </button>
        </p>
      </div>
    </div>
  );
}

export default Signup;